import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useI18N } from '../../context/I18NContext';
import { useSettings } from '../../context/SettingsContext';
import { useFlow } from '../../context/FlowContext';
import useWidgetStore from '../../store/useWidgetStore';
import useDataStore from '../../store/useDataStore';
import { appointmentsInOrder, apptypeNamesFor, capacityGroupNamesFrom, capacityLabelFor, resourceNamesFor } from '../../helpers/appointment';
import { isDesktop, isMobile } from '../../../../helpers/browser';
import ConfigHolder from '../../config/ConfigHolder';
import NumberSpinnerField from '../fields/NumberSpinnerField';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { useNavigate } from 'react-router-dom';
import { moneyFormat } from '../../helpers/general';
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    // verticalAlign: 'top',
    textAlign: 'right',
    width: '6em',
    // A long product/apptype name in the row's other cell has nothing bounding its own width, so
    // the table's auto layout shrinks this column to make room for it - without this, that leaves
    // "€ 75,-" narrower than its own text and wraps it onto two lines instead.
    whiteSpace: 'nowrap'
}));
export default function CartSimple(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const { t, ts } = useI18N();
    const theme = useTheme();
    const flow = useFlow();
    const { enabled, setEnabled, cart, setCart, booking, language } = useWidgetStore();
    const { apptypes, products, setProducts, getProduct } = useDataStore();
    const { showSelectedResource, staticData, orientation, allowMultipleAppointments } = useSettings();
    const navigate = useNavigate();
    const [hasDeletables, setHasDeletables] = useState(false);
    const [oldLines, setOldLines] = useState([]);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [smallMode, setSmallMode] = useState(false);
    useEffect(() => {
        if (!enabled.product) {
            setEnabled(Object.assign(Object.assign({}, enabled), { product: true }));
        }
        setSmallMode(orientation === "vertical" || isMobile);
    }, []);
    const capacityGroupNames = useMemo(() => capacityGroupNamesFrom(flow.views), [flow.views]);
    // What each appointment (not just the first) was actually booked with - a second
    // "Nog een afspraak toevoegen" appointment can be under a different apptype and a different
    // capacity, so this is computed per appointment rather than once for the whole booking.
    const appointmentInfo = (appointment) => {
        var _a;
        return ({
            apptypeStr: apptypeNamesFor(appointment, apptypes).join(', '),
            resourceStr: resourceNamesFor(appointment, (_a = staticData === null || staticData === void 0 ? void 0 : staticData.resources) !== null && _a !== void 0 ? _a : []).join(', '),
            capacityStr: capacityLabelFor(appointment, capacityGroupNames, { t, ts }, 'cartSummary')
        });
    };
    // Same reasoning as appointmentInfo(): a second appointment can be under a different
    // apptype, so its image is resolved per appointment rather than once for the whole booking.
    const imageForAppointment = (appointment) => {
        var _a;
        let component = (_a = appointment.components) === null || _a === void 0 ? void 0 : _a[0];
        let appointmentType = apptypes.find((appointmentType) => { var _a; return appointmentType.id === ((_a = component === null || component === void 0 ? void 0 : component.appointmentTypes) === null || _a === void 0 ? void 0 : _a[0]); });
        if (!appointmentType || appointmentType.image.toLowerCase().includes('missing')) {
            return undefined;
        }
        return appointmentType.image.startsWith('https')
            ? appointmentType.image
            : `${ConfigHolder.apiBaseUrl}${appointmentType.image}`;
    };
    useEffect(() => {
        var _a;
        let total = 0;
        cart.lines.forEach((line) => {
            var _a;
            if (line.productType !== "appointment" && !(["subscription", "membership"].includes(line.productType) && ((_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0)) {
                total += line.price * line.quantity;
            }
        });
        if (((_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            booking.appointments.map((appointment) => {
                total += appointment.price;
            });
        }
        setInvoiceTotal(total);
    }, [cart.lines, booking.appointments]);
    useEffect(() => {
        if (cart.lines) {
            setHasDeletables(!!(cart.lines.find((line) => { var _a; return line.origin !== "requiredPurchase" && line.productType !== "appointment" && !(["subscription", "membership"].includes(line.productType) && ((_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0); })));
            if (cart.lines.length !== oldLines.length) {
                const element = document.getElementById('widget-cart-simple');
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        // @ts-ignore
                        container: 'nearest'
                    });
                }
            }
            setOldLines(cart.lines);
        }
        else {
            setHasDeletables(false);
        }
    }, [cart.lines]);
    const handleRowClick = () => { };
    const handleDeleteLine = (e) => {
        let productId = parseInt(e.target.closest('button').getAttribute('rel'));
        if (productId > 0) {
            let product = getProduct(productId);
            if (product) {
                if (product && props.selectCallback) {
                    props.selectCallback(product);
                }
                if (product.trackStock) { // explicitly not undefined
                    let _lineItem = cart.lines.find((line) => line.productId === productId);
                    let _products = products.map((_product) => {
                        if (_product.id === productId && _product.trackStock) {
                            _product.stock = _lineItem === null || _lineItem === void 0 ? void 0 : _lineItem.quantity;
                        }
                        return _product;
                    });
                    setProducts(_products);
                }
                setCart(Object.assign(Object.assign({}, cart), { lines: cart.lines.filter(a => a.productId !== productId &&
                        a.requiredBy !== productId) }));
            }
        }
    };
    const onChangeQuantity = (value, name) => {
        let line = cart.lines.find((line) => parseInt(name) === line.productId);
        if (line) {
            let _lines = [];
            let diff = value - line.quantity; // can be +1 or -1 when using the buttons, or any integer if the text input is used
            let _products = products.map((_product) => {
                if (_product.id === (line === null || line === void 0 ? void 0 : line.productId) && _product.trackStock) {
                    _product.stock = _product.stock - diff; // a positive change requires a substraction
                }
                return _product;
            });
            setProducts(_products);
            cart.lines.forEach((line) => {
                if (line.productId === parseInt(name)) {
                    line.quantity = value;
                }
                _lines.push(line);
            });
            setCart(Object.assign(Object.assign({}, cart), { lines: _lines }));
            // const element = document.getElementById('widget-cart-simple')
            // if (element) {
            //     element.scrollIntoView({
            //         behavior: 'smooth',
            //         block: 'start',
            //         // @ts-ignore
            //         container: 'nearest'
            //     })
            // }
        }
    };
    const goTo = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        navigate(`/oa_widget_${path}`);
    };
    const renderLine = (line, product, i) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        if (!product) {
            return (_jsx(Fragment, {}, i));
        }
        let imageUrl = product.image.startsWith('https')
            ? `${product.image}`
            : `${ConfigHolder.apiBaseUrl}${product.image}`;
        return (_jsxs(Fragment, { children: [_jsxs(TableRow, { rel: i.toString(), onClick: handleRowClick, sx: {
                        backgroundColor: theme.palette.background.default,
                        height: '90px',
                        maxHeight: '90px'
                    }, children: [!imageUrl.includes('missing') && (_jsx(TableCell, { sx: {
                                //width: '64px' 
                                padding: '6px',
                                width: smallMode ? '80px' : '3em',
                                maxWidth: smallMode ? '80px' : '3em'
                            }, children: _jsx(Box, { children: _jsx("img", { src: `${imageUrl}?w=350&fit=scale`, title: product.name, style: {
                                        // width: '64px',
                                        maxWidth: '64px',
                                        maxHeight: '6em'
                                    } }) }) })), _jsxs(TableCell, { colSpan: smallMode ? 4 : 1, sx: Object.assign({}, (smallMode && {
                                maxWidth: '200px',
                            })), children: [_jsx(Typography, { gutterBottom: true, variant: "h5", component: "div", sx: Object.assign(Object.assign({ fontSize: '1rem', overflow: 'hidden' }, (smallMode && {
                                        maxWidth: '200px',
                                    })), { textOverflow: "ellipsis", whiteSpace: "nowrap" }), children: line.name }), product.subtitle && (_jsx(Typography, { variant: "body2", sx: Object.assign({ color: 'text.secondary', overflow: 'hidden', 
                                        // maxWidth: '150px',
                                        textOverflow: "ellipsis", whiteSpace: "nowrap" }, (smallMode && {
                                        maxWidth: '200px',
                                    })), children: product.subtitle }))] }), !smallMode && (_jsxs(_Fragment, { children: [_jsx(StyledTableCell, { sx: {
                                        width: '8em',
                                        maxWidth: '10em',
                                        minWidth: '8em'
                                        // width: '5em'
                                    }, children: line.productType === 'product' && line.origin !== "requiredPurchase" && !["summary"].includes((_c = (_b = (_a = flow.currentView) === null || _a === void 0 ? void 0 : _a.route) === null || _b === void 0 ? void 0 : _b.route) !== null && _c !== void 0 ? _c : "") && (_jsx(Box, { children: _jsx(NumberSpinnerField, { product: product, max: product.trackStock ? (product.stock + line.quantity) : 99, name: line.productId, defaultValue: line.quantity, min: 1, onChange: onChangeQuantity }) })) }), _jsx(StyledTableCell, { sx: { width: '4em' }, children: moneyFormat(line.quantity * line.price) }), hasDeletables && (_jsx(TableCell, { sx: { width: '2em' }, align: "right", children: line.origin !== "requiredPurchase" && !["summary"].includes((_f = (_e = (_d = flow.currentView) === null || _d === void 0 ? void 0 : _d.route) === null || _e === void 0 ? void 0 : _e.route) !== null && _f !== void 0 ? _f : "") && (_jsx(IconButton, { onClick: handleDeleteLine, rel: `${line.productId}`, "aria-label": "edit", size: "medium", children: _jsx(DeleteIcon, { sx: { color: theme.palette.text.secondary } }) })) }))] }))] }, line.name), smallMode && (_jsx(TableRow, { rel: i.toString(), onClick: handleRowClick, sx: {
                        backgroundColor: theme.palette.background.default
                    }, children: _jsxs(_Fragment, { children: [_jsxs(StyledTableCell, { colSpan: 4, sx: { width: '8em', maxWidth: '10em', textAlign: 'left', fontSize: '1.25rem', fontWeight: 'bolder' }, children: [line.productType === 'product' && line.origin !== "requiredPurchase" && !["summary"].includes((_j = (_h = (_g = flow.currentView) === null || _g === void 0 ? void 0 : _g.route) === null || _h === void 0 ? void 0 : _h.route) !== null && _j !== void 0 ? _j : "") && (_jsx(NumberSpinnerField, { product: product, max: product.trackStock ? (product.stock + line.quantity) : 99, name: line.productId, defaultValue: line.quantity, min: 1, onChange: onChangeQuantity })), line.origin !== "requiredPurchase" && !["summary"].includes((_m = (_l = (_k = flow.currentView) === null || _k === void 0 ? void 0 : _k.route) === null || _l === void 0 ? void 0 : _l.route) !== null && _m !== void 0 ? _m : "") && (_jsx(IconButton, { onClick: handleDeleteLine, rel: `${line.productId}`, "aria-label": "edit", size: "medium", children: _jsx(DeleteIcon, { sx: { color: theme.palette.text.secondary } }) }))] }), _jsx(TableCell, { sx: {
                                    textAlign: "right"
                                }, children: _jsx(Box, { sx: { marginLeft: 1, display: 'inline-block' }, children: moneyFormat(line.quantity * line.price) }) })] }) }, `${line.name}_edit`)), (_o = cart.lines) === null || _o === void 0 ? void 0 : _o.map((requiredLine, j) => {
                    // let product = getProduct(requiredLine.productId)
                    if (requiredLine.origin !== "requiredPurchase" || requiredLine.requiredBy !== line.productId) {
                        return (_jsx(Fragment, {}, `${i}_${j}`));
                    }
                    return (_jsxs(TableRow, { sx: {
                            backgroundColor: theme.palette.background.default,
                            height: '32px',
                            maxHeight: '32px'
                        }, children: [_jsx(TableCell, {}), _jsxs(TableCell, { colSpan: smallMode ? 3 : 2, sx: Object.assign({ 
                                    // maxWidth: '240px',
                                    textOverflow: 'ellipsis', overflow: 'hidden' }, (isDesktop && {
                                // whiteSpace: 'nowrap'
                                })), children: [" ", requiredLine.name] }), _jsx(StyledTableCell, { children: moneyFormat(requiredLine.price) }), !smallMode && (_jsx(TableCell, {}))] }, `${i}_${j}`));
                })] }, line.productId));
    };
    // Which apptype ids have already had their image shown in the appointment lines below -
    // each unique apptype gets the image once, on its first line, not once per appointment.
    const shownApptypeImageIds = new Set();
    return (_jsx(_Fragment, { children: _jsx(TableContainer, { id: "widget-cart-simple", sx: {
                marginBottom: 4,
                borderRadius: (props === null || props === void 0 ? void 0 : props.cartSummary) ? '0 0 4px 4px' : '4px',
                // overflowX: 'hidden'
            }, component: Paper, children: _jsx(Table, { size: "small", "aria-label": t('Overzicht van je winkelwagen', 'cartSimple'), children: _jsxs(TableBody, { sx: {
                        "& .MuiTableCell-body": {
                            fontSize: '1rem'
                        }
                    }, children: [((_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0 && (_jsxs(_Fragment, { children: [_jsx(TableRow, { sx: {
                                        backgroundColor: theme.palette.background.default
                                    }, children: _jsx(TableCell, { colSpan: smallMode ? 6 : 5, children: _jsxs(Typography, { variant: "h5", sx: {
                                                fontWeight: 500,
                                                fontSize: '1.1rem',
                                                marginTop: 1,
                                                position: "relative"
                                            }, children: [t(allowMultipleAppointments && ((_b = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _b === void 0 ? void 0 : _b.length) > 1 ? "Jouw afspraken" : "Jouw afspraak"), _jsx(IconButton, { onClick: goTo, rel: 'index', "aria-label": "edit", size: "medium", sx: {
                                                        position: "absolute",
                                                        top: "-8px",
                                                        right: "-16px"
                                                    }, children: _jsx(EditIcon, { sx: { color: theme.palette.text.secondary } }) })] }) }) }, "appointment-line-1"), appointmentsInOrder(booking.appointments).map((appointment, i) => {
                                    var _a, _b, _c;
                                    // Multiple appointments under the same apptype (e.g. 2x "Alleen door
                                    // Chriet") only need the image on the first one - repeating it on
                                    // every line just repeats the same picture. The column itself still
                                    // reserves the image's width on every such line though (just empty),
                                    // so the text stays aligned under the row that does show it. Only an
                                    // apptype with no image at all drops the column, for every one of its
                                    // lines, and those go flush left.
                                    const apptypeId = (_c = (_b = (_a = appointment.components) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.appointmentTypes) === null || _c === void 0 ? void 0 : _c[0];
                                    const alreadyShown = apptypeId !== undefined && shownApptypeImageIds.has(apptypeId);
                                    if (apptypeId !== undefined) {
                                        shownApptypeImageIds.add(apptypeId);
                                    }
                                    const image = imageForAppointment(appointment);
                                    const info = appointmentInfo(appointment);
                                    return (_jsxs(TableRow, { rel: "appointment", onClick: handleRowClick, sx: {
                                            backgroundColor: theme.palette.background.default,
                                            height: '90px',
                                            maxHeight: '90px'
                                        }, children: [image && (_jsx(TableCell, { sx: { padding: '6px' }, children: !alreadyShown && (_jsx(Box, { children: _jsx("img", { src: `${image}?w=350&fit=scale`, title: info.apptypeStr, style: { width: '64px' } }) })) })), _jsxs(TableCell, { colSpan: image ? 3 : 4, children: [_jsx(Typography, { variant: "h5", sx: { fontSize: '1rem', marginBottom: '0.35em' }, children: dayjs(appointment.date).locale(language).format('dddd D MMMM HH:mm') }), _jsx(Typography, { variant: "body2", sx: { color: 'text.secondary' }, children: info.apptypeStr }), showSelectedResource && info.resourceStr !== '' && (_jsx(Typography, { variant: "body2", sx: { color: 'text.secondary' }, children: info.resourceStr })), info.capacityStr !== '' && (_jsx(Typography, { variant: "body2", sx: { color: 'text.secondary' }, children: info.capacityStr }))] }), _jsx(TableCell, { sx: { width: '4em' }, align: "right", children: moneyFormat(appointment.price) })] }, `appointment-${i}`));
                                }), (_c = cart.lines) === null || _c === void 0 ? void 0 : _c.map((line, j) => {
                                    let product = getProduct(line.productId);
                                    if (line.origin !== "requiredPurchase" || line.requiredBy !== "appointment") {
                                        return (_jsx(Fragment, {}, `appointment_${j}`));
                                    }
                                    // Not tied to one specific appointment, so aligned against the first
                                    // appointment's own image column, same as before.
                                    const hasImage = !!imageForAppointment(booking.appointments[0]);
                                    return (_jsxs(TableRow, { sx: {
                                            backgroundColor: theme.palette.background.default,
                                            height: '32px',
                                            maxHeight: '32px'
                                        }, children: [hasImage && (_jsx(TableCell, {})), _jsxs(TableCell, { colSpan: hasImage
                                                    ? (smallMode
                                                        ? 3
                                                        : 2)
                                                    : (smallMode
                                                        ? 4
                                                        : 3), sx: Object.assign({ 
                                                    // maxWidth: '240px',
                                                    textOverflow: 'ellipsis', overflow: 'hidden' }, (isDesktop && {
                                                // whiteSpace: 'nowrap'
                                                })), children: [" ", line.name] }), _jsx(StyledTableCell, { children: moneyFormat(line.price) }), !smallMode && (_jsx(TableCell, {}))] }, `appointment_${j}`));
                                }), hasDeletables && (_jsx(TableRow, { sx: {
                                        backgroundColor: theme.palette.background.default
                                    }, children: _jsx(TableCell, { colSpan: smallMode ? 6 : 5, children: _jsxs(Typography, { variant: "h5", sx: {
                                                fontWeight: 500,
                                                fontSize: '1.1rem',
                                                marginTop: 2,
                                                position: "relative"
                                            }, children: [t("Extra producten"), ["summary"].includes((_f = (_e = (_d = flow.currentView) === null || _d === void 0 ? void 0 : _d.route) === null || _e === void 0 ? void 0 : _e.route) !== null && _f !== void 0 ? _f : "") && (_jsx(IconButton, { onClick: goTo, rel: 'shoppingCart', "aria-label": "edit", size: "medium", sx: {
                                                        position: "absolute",
                                                        top: smallMode ? "-8px" : "-16px",
                                                        right: smallMode ? "-16px" : "0"
                                                    }, children: _jsx(EditIcon, { sx: { color: theme.palette.text.secondary } }) }))] }) }) }, "appointment-line-3"))] })), ((_h = (_g = booking.appointments) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 0) === 0 && (_jsx(_Fragment, { children: hasDeletables && (_jsx(TableRow, { sx: {
                                    backgroundColor: theme.palette.background.default
                                }, children: _jsx(TableCell, { colSpan: smallMode ? 6 : 5, children: _jsxs(Typography, { variant: "h5", sx: {
                                            fontWeight: 500,
                                            fontSize: '1.1rem',
                                            marginTop: 2,
                                            position: "relative"
                                        }, children: [t("Jouw winkelwagen"), ["summary"].includes((_l = (_k = (_j = flow.currentView) === null || _j === void 0 ? void 0 : _j.route) === null || _k === void 0 ? void 0 : _k.route) !== null && _l !== void 0 ? _l : "") && (_jsx(IconButton, { onClick: goTo, rel: 'shoppingCart', "aria-label": "edit", size: "medium", sx: {
                                                    position: "absolute",
                                                    top: smallMode ? "-8px" : "-16px",
                                                    right: smallMode ? "-16px" : "0"
                                                }, children: _jsx(EditIcon, { sx: { color: theme.palette.text.secondary } }) }))] }) }) }, "appointment-line-4")) })), (_m = cart.lines) === null || _m === void 0 ? void 0 : _m.map((line, i) => {
                            let product = getProduct(line.productId);
                            if (line.origin === "requiredPurchase") {
                                return (_jsx(Fragment, {}, `${line.productId}_${i}`));
                            }
                            return (_jsx(Fragment, { children: renderLine(line, product, i) }, `${line.productId}_${i}`));
                        }), invoiceTotal > 0 && (_jsxs(TableRow, { sx: {
                                color: theme.palette.secondary.contrastText,
                                backgroundColor: theme.palette.secondary.main,
                                height: '50px',
                                minHeight: '50px',
                            }, children: [_jsxs(TableCell, { colSpan: smallMode ? 4 : 3, sx: {
                                        textAlign: 'right',
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem !important'
                                    }, children: [t('Totaal', 'cartSummary'), ":"] }), _jsx(StyledTableCell, { sx: {
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem !important'
                                    }, children: moneyFormat(invoiceTotal) }), !smallMode && (_jsx(TableCell, {}))] }, "total"))] }) }) }) }));
}
